/**
 * Estimate Header Component
 * BuilderBel Construction Project Management System
 *
 * Displays estimate summary totals in a header format
 */

import React from 'react';
import { DollarSign, Percent, Calculator } from 'lucide-react';

export interface EstimateHeaderProps {
  subtotalDirect: number;
  totalMarkup: number;
  grandTotal: number;
  subtotalLabor: number;
  subtotalMaterial: number;
  subtotalEquipment: number;
  isLoading?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
};

const EstimateHeader: React.FC<EstimateHeaderProps> = ({
  subtotalDirect,
  totalMarkup,
  grandTotal,
  subtotalLabor,
  subtotalMaterial,
  subtotalEquipment,
  isLoading = false,
}) => {
  const markupPercentage = subtotalDirect > 0 ? ((totalMarkup || 0) / subtotalDirect) * 100 : 0;

  const stats = [
    {
      label: 'Subtotal',
      value: formatCurrency(subtotalDirect),
      icon: Calculator,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Markups',
      value: formatCurrency(totalMarkup),
      subValue: `${formatNumber(markupPercentage)}%`,
      icon: Percent,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: 'Grand Total',
      value: formatCurrency(grandTotal),
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      highlight: true,
    },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map(stat => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className={`${stat.highlight ? 'border-l-4 border-green-500 pl-4' : ''}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`p-1.5 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {stat.label}
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={`text-2xl font-semibold ${stat.highlight ? stat.color : 'text-gray-900'}`}
                >
                  {stat.value}
                </span>
                {stat.subValue && <span className="text-sm text-gray-500">{stat.subValue}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Cost Breakdown Bar */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
          <span>Cost Breakdown</span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-blue-500" />
              Labor
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-green-500" />
              Material
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-yellow-500" />
              Equipment
            </span>
          </div>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
          {subtotalDirect > 0 && (
            <>
              <div
                className="bg-blue-500 h-full"
                style={{ width: `${((subtotalLabor || 0) / subtotalDirect) * 100}%` }}
              />
              <div
                className="bg-green-500 h-full"
                style={{ width: `${((subtotalMaterial || 0) / subtotalDirect) * 100}%` }}
              />
              <div
                className="bg-yellow-500 h-full"
                style={{ width: `${((subtotalEquipment || 0) / subtotalDirect) * 100}%` }}
              />
            </>
          )}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-600">
          <span>{formatCurrency(subtotalLabor)}</span>
          <span>{formatCurrency(subtotalMaterial)}</span>
          <span>{formatCurrency(subtotalEquipment)}</span>
        </div>
      </div>
    </div>
  );
};

export default EstimateHeader;
